import { sumBy } from 'lodash';

import { CustomerTypeOption } from '../quotes/view-or-edit-bill-of-materials/dataForViewOrEditQuoteBillOfMaterialsForm';
import { TechnologyTypeCode } from '../shared/technologyTypeCode';
import { BillOfMaterialsItemFormModel } from './billOfMaterialsItemFormModel';

export type BillOfMaterialsUnitResponse = {
  billOfMaterialsUnitId: number;
  name: string;
  underfloorHeatingDescription: string | null;
  heatPumpDescription: string | null;
  solarDescription: string | null;
  quantity: number;
  underfloorHeatingItems: Array<BillOfMaterialsItemResponse>;
  solarItems: Array<BillOfMaterialsItemResponse>;
  heatPumpItems: Array<BillOfMaterialsItemResponse>;
};

export type BillOfMaterialsPartsById = { [partId in number]: BillOfMaterialsPartResponse };

export type BillOfMaterialsPartResponse = {
  partId: number;
  name: string;
  description: string;
  productType: string | null;
  costInPounds: number | null;
  quotePriceInPounds: number | null;
  developerPriceInPounds: number | null;
  homeownerPriceInPounds: number | null;
  installerPriceInPounds: number | null;
  merchantPriceInPounds: number | null;
  isInactive: boolean;
};

export type BillOfMaterialsItemResponse = {
  billOfMaterialsItemId: number;
  technologyTypeCode: TechnologyTypeCode;
  partId: number;
  displayIndex: number;
  quantity: number;
  costInPounds: number | null;
  priceInPounds: number | null;
  isDeleted: boolean;
};

export type BillOfMaterialsOptionalExtraResponse = {
  billOfMaterialsOptionalExtraId: number;
  technologyTypeCode: TechnologyTypeCode;
  underfloorHeatingOptionTypeCode: UnderfloorHeatingOptionTypeCode | null;
  partId: number;
  quantity: number;
};

export type UnderfloorHeatingOptionTypeCode = 'Control' | 'FloorConstruction' | 'Cylinder';

export const calculateTotalPriceBeforeDiscount = (
  isUsingLegacyPricingModel: boolean,
  items: Array<BillOfMaterialsItemFormModel>,
  partsById: BillOfMaterialsPartsById,
  customerTypeOption?: CustomerTypeOption,
) =>
  sumBy(
    items.filter(item => !item.isDeleted),
    item => {
      const part = item.partId == null ? null : partsById[item.partId];

      const priceInPounds =
        item.priceInPounds ||
        (part && getPartPriceBasedOnCustomerType(isUsingLegacyPricingModel, part, customerTypeOption)) ||
        0;
      const quantity = isNaN(Number(item.quantity)) ? 0 : Number(item.quantity);
      return priceInPounds * quantity;
    },
  );

export const getPartPriceBasedOnCustomerType = (
  isUsingLegacyPricingModel: boolean,
  part: BillOfMaterialsPartResponse,
  customerType?: CustomerTypeOption,
): number | null => {
  if (isUsingLegacyPricingModel) {
    return part.quotePriceInPounds;
  }

  if (customerType == null) {
    return null;
  }

  switch (customerType.pricingType) {
    case 'Developer':
      return part.developerPriceInPounds;
    case 'Homeowner':
      return part.homeownerPriceInPounds;
    case 'Installer':
      return part.installerPriceInPounds;
    case 'Merchant':
      return part.merchantPriceInPounds;
    default:
      throw new Error(`Unsupported customer type pricing type: ${customerType.pricingType}`);
  }
};

export const calculateTotalCost = (items: Array<BillOfMaterialsItemFormModel>, partsById: BillOfMaterialsPartsById) =>
  sumBy(
    items.filter(item => !item.isDeleted),
    item => {
      const part = item.partId == null ? null : partsById[item.partId];
      const costInPounds = item.costInPounds || (part && part.costInPounds) || 0;
      const quantity = isNaN(Number(item.quantity)) ? 0 : Number(item.quantity);
      return costInPounds * quantity;
    },
  );
